/**
 * useUserIdle Hook
 *
 * Detects when the user has been idle (no mouse, keyboard, scroll, or touch activity)
 * for a specified timeout duration.
 *
 * Usage:
 * ```tsx
 * const isIdle = useUserIdle(300000); // 5 minutes
 * if (isIdle) {
 *   // User has been inactive for 5 minutes
 * }
 * ```
 */

import { useEffect, useRef, useState, useCallback } from 'react';

// Events that indicate user activity
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'scroll', 'touchstart', 'click', 'wheel'] as const;

// Event listener options - must be the same for add and remove
const EVENT_OPTIONS: AddEventListenerOptions = { passive: true };

/**
 * Hook to detect user idle state
 *
 * @param timeoutMs - Duration of inactivity (in milliseconds) before considered idle
 * @param enabled - Whether idle detection is enabled (default: true)
 * @returns boolean - true if user is idle, false if active
 */
export function useUserIdle(timeoutMs: number, enabled: boolean = true): boolean {
  const [isIdle, setIsIdle] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Store timeoutMs in a ref to avoid recreating resetTimer on timeoutMs changes
  // This prevents unnecessary event listener re-registration
  const timeoutMsRef = useRef(timeoutMs);
  useEffect(() => {
    timeoutMsRef.current = timeoutMs;
  }, [timeoutMs]);

  // Stable resetTimer function that doesn't change when timeoutMs changes
  // Uses ref to get current timeout value
  const resetTimer = useCallback(() => {
    // User is active
    setIsIdle(false);

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout using current ref value
    timeoutRef.current = setTimeout(() => {
      setIsIdle(true);
    }, timeoutMsRef.current);
  }, []); // Empty deps - uses ref for timeoutMs

  useEffect(() => {
    if (!enabled) {
      setIsIdle(false);
      // Clear any existing timeout when disabled
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    // Add event listeners for activity detection
    // Use the same options object for both add and remove to ensure proper cleanup
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, resetTimer, EVENT_OPTIONS);
    });

    // Start initial timer
    resetTimer();

    // Cleanup - must pass same options to ensure listener is properly removed
    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, resetTimer, EVENT_OPTIONS);
      });

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [enabled, resetTimer]);

  return isIdle;
}

export default useUserIdle;
